
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, Mail, MessageSquare, Users, Phone } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useNavigationUtils } from '@/hooks/useNavigationUtils';

interface CallToActionSectionProps {
  t: any;
  content: any;
  currentLanguage: string;
}

const CallToActionSection: React.FC<CallToActionSectionProps> = ({ t, content, currentLanguage }) => {
  const navigate = useNavigate();
  const { getWhyFutureLevelPath } = useNavigationUtils();
  
  // Define the button click handler to navigate to "Why Future Level" page
  const handleWhyFutureLevelClick = () => {
    navigate(getWhyFutureLevelPath());
  };

  return (
    <section className="py-20 bg-gradient-to-b from-[#050509] to-[#090915]">
      <div className="container mx-auto px-4">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6 text-white">
            {content.ctaTitle}
          </h2>
          <p className="text-lg text-gray-300 mb-8">
            {content.ctaDescription}
          </p>
          
          {/* Icon-based contact section */}
          <div className="mt-12 bg-black/40 backdrop-blur-md p-8 rounded-xl border border-purple-500/20">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 text-left mb-10">
              <div className="flex flex-col items-center md:items-start space-y-3 p-4 hover:bg-purple-900/20 rounded-lg transition-colors">
                <div className="bg-purple-600/30 p-3 rounded-full">
                  <MessageSquare className="w-6 h-6 text-purple-300" />
                </div>
                <h3 className="font-semibold text-white text-lg">{t.personalConsultation || "Persönliche Beratung"}</h3>
                <p className="text-gray-300 text-sm">{t.personalConsultationDesc || "Individuelles Strategiegespräch für Ihre Produktvideo Kampagne"}</p>
              </div>
              
              <div className="flex flex-col items-center md:items-start space-y-3 p-4 hover:bg-purple-900/20 rounded-lg transition-colors">
                <div className="bg-purple-600/30 p-3 rounded-full">
                  <Users className="w-6 h-6 text-purple-300" />
                </div>
                <h3 className="font-semibold text-white text-lg">{t.targetAudience || "Zielgruppenfokus"}</h3>
                <p className="text-gray-300 text-sm">{t.targetAudienceDesc || "KI-optimierte Videos speziell für Ihre Schweizer Zielgruppe"}</p>
              </div>
              
              <div className="flex flex-col items-center md:items-start space-y-3 p-4 hover:bg-purple-900/20 rounded-lg transition-colors">
                <div className="bg-purple-600/30 p-3 rounded-full">
                  <Phone className="w-6 h-6 text-purple-300" />
                </div>
                <h3 className="font-semibold text-white text-lg">{t.support24h || "24h Support"}</h3>
                <p className="text-gray-300 text-sm">{t.support24hDesc || "Schnelle Antwort innerhalb eines Werktages für Ihre Video-Anfrage"}</p>
              </div>
              
              <div className="flex flex-col items-center md:items-start space-y-3 p-4 hover:bg-purple-900/20 rounded-lg transition-colors">
                <div className="bg-purple-600/30 p-3 rounded-full">
                  <Mail className="w-6 h-6 text-purple-300" />
                </div>
                <h3 className="font-semibold text-white text-lg">{t.freeAnalysis || "Kostenlose Analyse"}</h3>
                <p className="text-gray-300 text-sm">{t.freeAnalysisDesc || "Gratis Video-Marketing-Analyse für Ihren Brand"}</p>
              </div>
            </div>

            <Button
              onClick={handleWhyFutureLevelClick}
              className="bg-gradient-to-r from-purple-600 to-purple-800 hover:from-purple-700 hover:to-purple-900 text-white px-10 py-6 text-lg rounded-lg shadow-lg group"
              size="lg"
            >
              Warum Future Level
              <ArrowRight className="ml-2 w-6 h-6 group-hover:translate-x-1 transition-transform" />
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default CallToActionSection;
